import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from './ui/dialog';
import { Badge } from './ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Info,
  Calculator
} from 'lucide-react';
import { dailyAvailableCalculator, DailyAvailableSettings } from '../lib/calculator/dailyAvailable';

export function DailyAvailable() {
  const { storage, state } = useApp();
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  const accounts = storage.getAccounts();
  const transactions = storage.getTransactions();
  const categories = storage.getCategories();
  const budgetAllocations = storage.getBudgetAllocations();
  const schedules = storage.getSchedules();

  // Default settings - in a real app these would be user-configurable
  const settings: DailyAvailableSettings = {
    includeGoalsInDA: true,
    useOnlyVariableCategories: true,
    roundDownToWhole: true
  };

  const breakdown = dailyAvailableCalculator.calculate(
    accounts,
    transactions,
    categories,
    budgetAllocations,
    schedules,
    state.currentMonth,
    settings
  );

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDailyAvailableColor = (amount: number) => {
    if (amount >= 50) return 'text-green-600 dark:text-green-400';
    if (amount >= 20) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getDailyAvailableMessage = (amount: number, daysRemaining: number) => {
    if (amount === 0) {
      return `No discretionary funds available for the remaining ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
    }
    
    if (amount >= 100) {
      return `Great! You have plenty of flexibility in your budget`;
    } else if (amount >= 50) {
      return `Good spending cushion for daily expenses`;
    } else if (amount >= 20) {
      return `Moderate spending room - watch your expenses`;
    } else {
      return `Tight budget - be very careful with spending`;
    }
  };

  return (
    <>
      {/* Main Daily Available Card */}
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <DollarSign className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Daily Available</span>
                <Dialog open={showBreakdown} onOpenChange={setShowBreakdown}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                      <Info className="w-3 h-3" />
                    </Button>
                  </DialogTrigger>
                  <DailyAvailableBreakdown breakdown={breakdown} settings={settings} />
                </Dialog>
              </div>
              
              <div className={`text-3xl font-bold ${getDailyAvailableColor(breakdown.dailyAvailable)}`}>
                {formatCurrency(breakdown.dailyAvailable)}
              </div>
              
              <p className="text-xs text-muted-foreground mt-1">
                {getDailyAvailableMessage(breakdown.dailyAvailable, breakdown.daysRemaining)}
              </p>
            </div>

            <div className="text-right">
              <div className="flex items-center space-x-1 mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {breakdown.daysRemaining} day{breakdown.daysRemaining !== 1 ? 's' : ''} left
                </span>
              </div>
              
              <Badge variant="secondary" className="text-xs">
                Based on fixed commitments
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// Daily Available Breakdown Dialog
interface DailyAvailableBreakdownProps {
  breakdown: any;
  settings: DailyAvailableSettings;
}

function DailyAvailableBreakdown({ breakdown, settings }: DailyAvailableBreakdownProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const summary = dailyAvailableCalculator.getSummary(breakdown, settings);

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center">
          <Calculator className="w-5 h-5 mr-2" />
          Daily Available Breakdown
        </DialogTitle>
        <DialogDescription>
          How your daily spending amount is calculated
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Main Calculation */}
        <Card>
          <CardContent className="p-4">
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-primary">
                {formatCurrency(breakdown.dailyAvailable)}
              </div>
              <p className="text-sm text-muted-foreground">per day available</p>
            </div>

            <div className="space-y-3 text-sm">
              {/* Income Remaining */}
              {breakdown.incomeRemaining > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="w-4 h-4 text-green-600 mr-2" />
                    <span>Income remaining</span>
                  </div>
                  <span className="font-medium text-green-600">
                    +{formatCurrency(breakdown.incomeRemaining)}
                  </span>
                </div>
              )}

              {/* Variable Pool */}
              {breakdown.variablePoolRemaining > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 text-blue-600 mr-2" />
                    <span>Available now</span>
                  </div>
                  <span className="font-medium text-blue-600">
                    +{formatCurrency(breakdown.variablePoolRemaining)}
                  </span>
                </div>
              )}

              {/* Fixed Expenses */}
              {breakdown.fixedExpensesRemaining > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingDown className="w-4 h-4 text-red-600 mr-2" />
                    <span>Fixed expenses due</span>
                  </div>
                  <span className="font-medium text-red-600">
                    -{formatCurrency(breakdown.fixedExpensesRemaining)}
                  </span>
                </div>
              )}

              {/* Separator */}
              <hr className="my-3" />

              {/* Discretionary Pool */}
              <div className="flex items-center justify-between font-medium">
                <span>Discretionary pool</span>
                <span>{formatCurrency(breakdown.discretionaryPool)}</span>
              </div>

              {/* Days Remaining */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-muted-foreground mr-2" />
                  <span>Days remaining</span>
                </div>
                <span>{breakdown.daysRemaining}</span>
              </div>

              {/* Calculation */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {formatCurrency(breakdown.discretionaryPool)} ÷ {breakdown.daysRemaining} days
                  {settings.roundDownToWhole ? ' (rounded down)' : ''}
                </span>
                <span>=</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="p-4 bg-muted/30 rounded-lg">
          <p className="text-sm text-center">{summary}</p>
        </div>

        {/* Settings Note */}
        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Settings applied:</strong></p>
          <ul className="space-y-1 ml-2">
            <li>• Goals included: {settings.includeGoalsInDA ? 'Yes' : 'No'}</li>
            <li>• Variable categories only: {settings.useOnlyVariableCategories ? 'Yes' : 'No'}</li>
            <li>• Round down: {settings.roundDownToWhole ? 'Yes' : 'No'}</li>
          </ul>
        </div>
      </div>
    </DialogContent>
  );
}

// Compact version for dashboard
export function DailyAvailableCompact() {
  const { storage, state } = useApp();
  
  const accounts = storage.getAccounts();
  const transactions = storage.getTransactions();
  const categories = storage.getCategories();
  const budgetAllocations = storage.getBudgetAllocations();
  const schedules = storage.getSchedules();

  const settings: DailyAvailableSettings = {
    includeGoalsInDA: true,
    useOnlyVariableCategories: true,
    roundDownToWhole: true
  };

  const breakdown = dailyAvailableCalculator.calculate(
    accounts,
    transactions,
    categories,
    budgetAllocations,
    schedules,
    state.currentMonth,
    settings
  );

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDailyAvailableColor = (amount: number) => {
    if (amount >= 50) return 'text-green-600 dark:text-green-400';
    if (amount >= 20) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="text-center">
      <div className={`text-2xl font-bold ${getDailyAvailableColor(breakdown.dailyAvailable)}`}>
        {formatCurrency(breakdown.dailyAvailable)}
      </div>
      <p className="text-xs text-muted-foreground">
        {breakdown.daysRemaining} days • Fixed commitments
      </p>
    </div>
  );
}